import TextField from '../../ui/TextField';
import { useStore } from '../../../hooks/useStore';
import { FormEventHandler, useState } from 'react';
import {
  Button,
  ButtonVariants,
  Modes
} from 'stubben-edge-unified-frontend-react-components';
import IconCloseCircle from '../../icons/IconCloseCircle';
import FileField from '../../ui/FileField';
import Drawer from '../../ui/Drawer';
import IconEdit from '../../icons/IconEdit';
import DeterBrowserAutoFill from '../../ui/DeterBrowserAutoFill';
import { useForm } from 'react-form-reducer';
import api from '@api-client/client';
import { stripIfEmpty } from '../../../utils/object';
import EmptyState from '@ui/EmptyState/EmptyState';

interface FormInterface {
  profile_photo: File | null;
  name: string;
  last_name: string;
  email: string;
  telephone: string;
  current_password: string;
  password: string;
  password_confirmation: string;
  pin: string;
  confirm_pin: string;
}

const UpdateProfileDrawer = ({
  open,
  onClose
}: {
  open: boolean;
  onClose?: () => void;
}) => {
  const { authStore } = useStore();
  const user = authStore.user;
  const [showSuccess, setShowSuccess] = useState(false);
  const [isDeletePhoto, setDeletePhoto] = useState(false);
  const [showPin, setShowPin] = useState<boolean>(false);
  const userPin = user?.pin ? user?.pin : '';

  const {
    fields,
    setFields,
    setField,
    handleInputChange,
    errors,
    isBusy,
    setIsBusy
  } = useForm<FormInterface>({
    profile_photo: null,
    name: user?.name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    telephone: user?.telephone || '',
    current_password: '',
    password: '',
    password_confirmation: '',
    pin: '',
    confirm_pin: ''
  });

  const handleSubmit: FormEventHandler = (e) => {
    e.preventDefault();
    saveChanges();
  };

  const resetFields = () => {
    setFields({
      profile_photo: null,
      name: user?.name || '',
      last_name: user?.last_name || '',
      email: user?.email || '',
      telephone: user?.telephone || '',
      current_password: '',
      password: '',
      password_confirmation: '',
      pin: '',
      confirm_pin: ''
    });

    setDeletePhoto(false);
  };

  const saveChanges = () => {
    // Cancel if busy
    if (isBusy) return;
    // Set busy
    setIsBusy(true);

    const data = stripIfEmpty({
      id: user?.id,
      name: fields.name,
      last_name: fields.last_name,
      email: fields.email,
      telephone: fields.telephone || '',
      current_password: fields.current_password || null,
      password: fields.password || null,
      password_confirmation: fields.password_confirmation || null,
      pin: fields.pin || null,
      confirm_pin: fields.confirm_pin || null
    });

    if (
      isDeletePhoto ||
      (fields.profile_photo &&
        fields.profile_photo.name !==
          user?.profile_photo_path?.split('/').pop())
    )
      data.profile_photo = fields.profile_photo || '';

    // Post the data to the api
    api.user
      .updateUserProfile(data)
      .then(async () => {
        // All good
        await authStore.refreshUser();
        setShowSuccess(true);
      })
      .catch((err) => {
        // Process the backend validation errors if present
        if (err.response.data.details) {
          errors.set(err.response.data.details);
        } else {
          alert('Oops, something went wrong!');
        }
      })
      .finally(() => setIsBusy(false));
  };

  return (
    <Drawer
      open={open}
      onClose={() => {
        if (onClose) {
          // Check if onClose is defined
          onClose();
        }
        resetFields();
        setShowSuccess(false);
      }}
      notDismissible={isBusy}
      title="Edit Profile"
      headerIcon={<IconEdit />}
      position="right"
      actions={
        <div className="flex items-center justify-between">
          <Button
            variant={ButtonVariants.Tertiary}
            mode={Modes.Ghost}
            leftSlot={<IconCloseCircle />}
            disabled={isBusy}
            testIdentifier="update-profile-drawer-cancel"
            onClick={() => {
              if (onClose) {
                // Check if onClose is defined
                onClose();
              }
              resetFields();
              setShowSuccess(false);
            }}
          >
            Cancel
          </Button>

          {showSuccess ? (
            <Button
              onClick={() => {
                if (onClose) {
                  // Check if onClose is defined
                  onClose();
                }
                resetFields();
                setShowSuccess(false);
              }}
              className="w-56 max-w-full"
              testIdentifier="update-profile-drawer-done"
            >
              Done
            </Button>
          ) : (
            <Button
              onClick={saveChanges}
              loading={isBusy}
              testIdentifier="update-profile-drawer-save"
            >
              Save changes
            </Button>
          )}
        </div>
      }
    >
      {showSuccess ? (
        <>
          <h4 className="h5 mb-6 mt-4">
            The profile details have been updated successfully.
          </h4>
          <EmptyState
            image="/imgs/success-state.svg"
            imageClassName="!w-full !max-w-[80%]"
          />
        </>
      ) : (
        <>
          {open && user && (
            <form onSubmit={handleSubmit}>
              <DeterBrowserAutoFill />

              <h4 className="h4 my-2">Profile Picture</h4>
              <div className="input-file mb-4">
                <FileField
                  type="image"
                  name="profile_photo"
                  previewType="avatar"
                  defaultValue={user.profile_photo_path || undefined}
                  file={fields.profile_photo}
                  onChange={(file) => {
                    setField('profile_photo', file);
                    setDeletePhoto(file === null);
                  }}
                  error={errors.has('profile_photo')}
                  helperText={errors.get('profile_photo')}
                />
              </div>

              <h4 className="h4 mb-4 mt-6">Details</h4>
              <div className="-mx-2 block sm:flex">
                <div className="mb-4 sm:w-1/2 sm:px-2">
                  <TextField
                    label="First name"
                    name="name"
                    value={fields.name}
                    onInputChange={handleInputChange('name')}
                    error={errors.has('name')}
                    helperText={errors.get('name')}
                  />
                </div>
                <div className="mb-4 sm:w-1/2 sm:px-2">
                  <TextField
                    label="Last name"
                    name="last_name"
                    value={fields.last_name}
                    onInputChange={handleInputChange('last_name')}
                    error={errors.has('last_name')}
                    helperText={errors.get('last_name')}
                  />
                </div>
              </div>
              <div className="-mx-2 block sm:flex">
                <div className="mb-4 sm:w-1/2 sm:px-2">
                  <TextField
                    type="email"
                    label="Email address"
                    placeholder="Enter email address"
                    name="email"
                    value={fields.email}
                    onInputChange={handleInputChange('email')}
                    error={errors.has('email')}
                    helperText={errors.get('email')}
                  />
                </div>
                <div className="mb-4 sm:w-1/2 sm:px-2">
                  <TextField
                    type="text"
                    label="Mobile number"
                    placeholder="Enter mobile number"
                    name="telephone"
                    value={fields.telephone || ''}
                    onInputChange={handleInputChange('telephone')}
                    error={errors.has('telephone')}
                    helperText={errors.get('telephone')}
                  />
                </div>
              </div>

              <h4 className="h4 mb-4 mt-6">Change Password</h4>
              <div className="mb-4">
                <TextField
                  type="password"
                  label="Current Password"
                  showPasswordToggle
                  name="current_password"
                  value={fields.current_password}
                  onInputChange={handleInputChange('current_password')}
                  error={errors.has('current_password')}
                  helperText={errors.get('current_password')}
                />
              </div>
              <div className="-mx-2 block sm:flex">
                <div className="mb-4 sm:w-1/2 sm:px-2">
                  <TextField
                    type="password"
                    label="New Password"
                    showPasswordToggle
                    name="password"
                    value={fields.password}
                    onInputChange={handleInputChange('password')}
                    error={errors.has('password')}
                    helperText={errors.get('password')}
                  />
                </div>
                <div className="mb-4 sm:w-1/2 sm:px-2">
                  <TextField
                    type="password"
                    label="Confirm Password"
                    showPasswordToggle
                    name="password_confirmation"
                    value={fields.password_confirmation}
                    onInputChange={handleInputChange('password_confirmation')}
                    error={errors.has('password_confirmation')}
                    helperText={errors.get('password_confirmation')}
                  />
                </div>
              </div>

              {user?.pin_is_allowed === true && (
                <>
                  <h4 className="h4 mb-4 mt-6">SB Pro Terminal</h4>

                  <div className="-mx-2 block sm:flex">
                    <div className="mb-4 sm:w-1/2 sm:px-2">
                      <TextField
                        type="password"
                        label="Pin (optional)"
                        showPasswordToggle
                        name="pin"
                        value={fields.pin}
                        onInputChange={handleInputChange('pin')}
                        error={errors.has('pin')}
                        helperText={errors.get('pin')}
                      />
                    </div>

                    <div className="mb-4 sm:w-1/2 sm:px-2">
                      <TextField
                        type="password"
                        label="Confirm pin (optional)"
                        showPasswordToggle
                        name="confirm_pin"
                        value={fields.confirm_pin}
                        onInputChange={handleInputChange('confirm_pin')}
                        error={errors.has('confirm_pin')}
                        helperText={errors.get('confirm_pin')}
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <Button
                      onClick={() => setShowPin(!showPin)}
                      testIdentifier="update-profile-drawer-toggle-pin"
                    >
                      {showPin ? 'Hide PIN' : 'Show PIN'}
                    </Button>
                    {showPin && (
                      <span className="text-sm font-semibold">
                        PIN: {userPin}
                      </span>
                    )}
                  </div>
                </>
              )}
            </form>
          )}
        </>
      )}
    </Drawer>
  );
};

export default UpdateProfileDrawer;
